import { confirmComputerSend, sendingSettings } from './email-send'
import './messages.css'
import { stepBar, stepDate, type Step } from './enquiry-steps'
import { meaningIcon } from './meaning-icon'
import { copyText } from './copied'
// Book again, stage 1, the free way (0094; Liam, 27-28 Oct 2026; docs/plans/book-again/BUILD-SPEC.md, FINAL DECISIONS;
// drawings https://claude.ai/artifact/Ls6ubS4byV3ehhH2oWWi9o, copies in docs/plans/book-again/drawings/).
// After a new customer's first clean the owner checks on WhatsApp whether they want cleaning again and how often, with
// their price for each. Roundly opens WhatsApp with the words typed; the owner presses Send there and taps her answer back
// in here. Owner only, once per customer, payment messages first.
//
// One card, drawn the same on Messages (its own "Book again" group) and the customer's page (under Price and Every):
//   waits    after her payment: "What happens next", three picture tiles, "Go to her payment in Owed" (drawing 7.1/6.2)
//   ready    Check with Sarah (6.2b), opening the pop-up (FreeB for Just once / Ad hoc / Not set, RegularA for regulars)
//   opened   WhatsApp opened: Did you send Sarah the check? I sent it / Didn't send it (6.4)
//   quiet    You sent it: waiting for her answer, with the answer buttons (her page only)
//   ask      3 days on: Any reply from Sarah? (6.5)
//   done     the finished line (her page only)
// Her answer (6.6): "You saved her choice", price and how often, and Roundly's suggested next clean, with Undo.
import { api, ApiError, changeNow, mutate, type Ack } from './api'
import { getMe } from './me'
import { bookAgainOn, businessSettings, hasBusinessSettings, wordMessage } from './business-settings'
import { onAPhone, whatsappHref, whatsappTarget } from './rebook-message'
import { initialsOf, lineDrawing, type Drawing } from './picture-tiles'
import { clear, el, mondayOf, money, parseMoney, shortDate, toast, todayISO } from './ui'
import { offerUndo } from './undo'
import { currentTrade } from './trade'
import { bookAgainPrice } from './wa-template-words'

export type BACardState = 'waits' | 'ready' | 'opened' | 'quiet' | 'ask' | 'done'
  | 'wa_unsure' | 'wa_failed' | 'wa_waiting' | 'wa_held' | 'wa_answered' | 'wa_no' | 'wa_undone'
export type BAKind = 'regular' | 'once' | 'adhoc' | 'unset'
export interface BATile { weeks: number; pence: number | null; mine: boolean; out?: boolean }
export interface BAOffer {
  id: number; state: 'opened' | 'sent' | 'answered' | 'kept' | 'declined' | 'other' | 'sending' | 'unknown' | 'failed' | 'undone_tap'; version: number; prices_json: string; message: string | null
  picked_weeks: number | null; picked_pence: number | null; opened_by: string | null; opened_at: string | null; sent_on: string | null; answered_at: string | null; created_at: string
  // On WhatsApp with tap buttons (0099).
  declined_reply_recorded?: boolean; way?: 'free' | 'tap'; generation?: number; wa_status?: string | null; wa_error?: string | null; sent_at?: string | null; held?: number; no_thanks?: number
}
// What would go on WhatsApp with tap buttons, from GET ?wa=1 (functions/lib/book-again-wa.ts TapFacts).
interface WaFacts { anchor: number; tiles: { weeks: number; pence: number }[]; insidePence: number | null; words: string; nextAt: string | null; key: string }
const pickedOften = (weeks: number | null): string => weeks === 0 ? 'just occasionally' : weeks ? `every ${weeks} weeks` : 'a choice that needs checking'
type WaWay = { ok: true; facts: WaFacts } | { ok: false; code: string; why: string }
interface HeldTap { id: number; weeks: number | null; pence: number | null; why: string | null; at: string }
export interface BACard {
  customer_id: number; name: string; customer_version: number; kind: BAKind; weeks: number | null; reg_price_pence: number | null
  insides_every: boolean; inside_pence: number | null; first_paid?: boolean; payment_recorded?: boolean; payment_clean_ids?: number[]; first_clean_id: number; first_date: string | null; first_pence: number | null; state: BACardState
  counted: boolean; wa: string | null; tiles: BATile[]; offer: BAOffer | null
  manual?: boolean; done_dates?: string[]
}

// ---- what Messages knows ---------------------------------------------------------------------------------------------
let known: { on: boolean; cards: BACard[]; count: number } | null = null

// Owner only, and only while the round has it on.
export function bookAgainShown(): boolean {
  return getMe()?.role === 'owner' && hasBusinessSettings() && bookAgainOn()
}

export async function loadBookAgain(): Promise<boolean> {
  if (!bookAgainShown()) { known = null; return true }
  try {
    known = await api<{ on: boolean; cards: BACard[]; count: number }>('/api/book-again', 'book-again')
    return true
  } catch {
    // No signal: keep what was last known rather than say nobody is waiting.
    return false
  }
}

// What joins the Messages count: ready, WhatsApp opened, and 3 days on. A card waiting on her payment never counts.
export function bookAgainCount(): number {
  return bookAgainShown() && known?.on ? known.count : 0
}

// ---- words -----------------------------------------------------------------------------------------------------------
const first = (name: string): string => name.trim().split(/\s+/)[0] || name
const her = (c: BACard): string => first(c.name) // their first name: never a guessed she or he

function oftenWords(c: Pick<BACard, 'kind' | 'weeks'>): string {
  if (c.kind === 'regular' && c.weeks) return `Every ${c.weeks} weeks`
  if (c.kind === 'regular') return 'Regular'
  if (c.kind === 'once') return 'Just once'
  if (c.kind === 'adhoc') return 'Ad hoc'
  return 'How often not set'
}
// Their own word for "leave it", on the lemon answer (FINAL 10).
function leaveWords(c: BACard): string {
  if (c.kind === 'regular' && c.weeks) return `Keep every ${c.weeks} weeks`
  if (c.kind === 'regular') return 'No reply, keep them as they are'
  if (c.kind === 'once') return 'No reply, leave as Just once'
  if (c.kind === 'adhoc') return 'No reply, leave as Ad hoc'
  return 'No reply, leave it as it is'
}
const pounds = (p: number | null): string => (p == null ? 'Add price' : money(p))
const tileLabel = (t: BATile, c: BACard): string => (t.mine && c.kind === 'regular' ? 'Their choice now' : `Every ${t.weeks} weeks`)

// The work in the round's words: "your windows cleaned again" on a window round.
function againWords(): string {
  const t = currentTrade()
  if (t === 'window_cleaning') return 'your windows cleaned again'
  if (t === 'gardening') return 'us back for the garden'
  return 'us back again'
}

// The message, in the round's voice, signed by the owner (BUILD-SPEC FINAL 6). Only the tiles left in are named.
export function bookAgainMessage(c: BACard, tiles: BATile[], today = todayISO()): string {
  const on = tiles.filter((t) => !t.out && t.pence != null && Number.isInteger(t.weeks) && t.weeks >= 1 && t.weeks <= 52)
  const nums = on.map((t) => String(t.weeks))
  const stop = "Reply STOP if you'd rather not hear from us."
  // In the owner's own wording from More > Your messages when they have changed it (Liam, 28 Oct 2026: "Add it").
  const values: Record<string, string> = {
    'first name': her(c),
    thanks: c.first_date === today ? 'thanks for having us today' : 'thanks for having us',
    // Insides every visit: these prices are the outside's, and the message says so (Astra's build review 8).
    insides: c.insides_every && c.inside_pence ? ` These prices are for the outside; your insides stay at ${money(c.inside_pence)}.` : '',
    reply: nums.length === 1 ? nums[0]! : `${nums.slice(0, -1).join(', ')} or ${nums[nums.length - 1]}`,
    'your name': hasBusinessSettings() ? businessSettings().booking_signoff : '',
  }
  const mine = c.kind === 'regular' ? on.find((t) => t.mine) : undefined
  let text: string
  if (mine) {
    const others = on.filter((t) => !t.mine).map((t) => `Every ${t.weeks} weeks: ${money(t.pence)}`)
    text = wordMessage('book_again_regular', { ...values, weeks: String(mine.weeks), price: money(mine.pence),
      others: others.length ? ` If another suits you better: ${others.join(', ')}.` : '',
      'or nothing': mine.pence === c.reg_price_pence ? `, or nothing if ${mine.weeks} weeks is fine` : '' })
  } else {
    text = wordMessage('book_again', { ...values, again: againWords(), prices: on.map((t, i) => `${i === 0 ? 'Every' : 'every'} ${t.weeks} weeks ${money(t.pence)}`).join(', ') })
  }
  // The STOP line always goes with it, whatever the wording (FINAL 6).
  return /\bSTOP\b/.test(text) ? text : `${text.trimEnd()}\n\n${stop}`
}

// ---- the card --------------------------------------------------------------------------------------------------------
// The name opens her page: a link, so it is underlined (FINAL 13; links.css a.name-link).
function nameLink(c: BACard): HTMLElement {
  return el('a', { class: 'name-link ba-name', href: `#/customer/${c.customer_id}` }, c.name)
}

function ring(drawing: Drawing, size = 20): HTMLElement {
  return el('span', { class: 'ba-ring', 'aria-hidden': 'true' }, lineDrawing(drawing, size))
}

const sentAgo = (on: string | null, today: string): string => {
  if (!on) return 'You sent it'
  const days = Math.round((Date.parse(today) - Date.parse(on)) / 86_400_000)
  return days <= 0 ? 'You sent it today' : days === 1 ? 'You sent it yesterday' : `You sent it ${days} days ago`
}

// 'step': inside the customer page's Up next, which gives the step its own title (up-next.ts).
export interface CardHooks { reload: () => void; where: 'messages' | 'customer' | 'step' }

// One evidence-based bar on both hosts, including Up next. Completion never fills missing history.
const completedBookAgain = new Map<string, boolean>()
function bookAgainSteps(c: BACard): HTMLElement {
  const o = c.offer
  const payment = c.payment_recorded === true
  const sent = !!o && !!(o.sent_at || o.sent_on) && !['opened', 'sending', 'unknown', 'failed'].includes(o.state)
    // A tap offer the owner took over and confirmed with I sent it myself (wa_status maybe, then sent) is their record too;
    // Leave it never reaches sent, so it stays grey (review, 2 Nov 2026).
    && (o.way !== 'tap' || ['sent', 'delivered', 'read'].includes(o.wa_status ?? '') || o.wa_status === 'maybe' && o.state !== 'declined')
  const answered = o?.state === 'answered' || o?.state === 'declined' && o.declined_reply_recorded === true
  const ended = !!o && ['answered', 'kept', 'declined', 'other'].includes(o.state)
    || c.state === 'done'
  const facts = [payment, sent, answered]
  const next = ended ? -1 : c.state === 'waits' ? 0
    : ['ready', 'opened', 'wa_unsure', 'wa_failed'].includes(c.state) ? 1 : 2
  const identity = `${getMe()?.businessId}:${getMe()?.email}:${c.customer_id}:${o?.id ?? 'new'}`
  const complete = facts.every(Boolean)
  const justCompleted = completedBookAgain.get(identity) === false && complete
  completedBookAgain.set(identity, complete)
  const when = [null, o?.sent_at || o?.sent_on, o?.answered_at]
  const steps = ['Their payment', `Check with ${her(c).trim() || 'them'}`, 'Their answer'].map((word, i): Step => ({
    key: ['payment', 'check', 'answer'][i]!, word,
    state: facts[i] ? 'done' : i === next ? 'next' : ended || i < next ? 'unknown' : 'later',
    turn: i === 2 && (c.state === 'quiet' || c.state === 'wa_waiting') ? 'them' : 'you',
    ...(facts[i] && when[i] ? { when: stepDate(when[i]!) } : {}),
    ...(facts[i] ? { detail: [
      `${c.first_paid ? 'Payment recorded as paid' : 'Payment no longer holds up Book again'}.${when[i] ? '' : ' Date not recorded.'}`,
      `Book again message recorded as sent.${when[i] ? '' : ' Date not recorded.'}`,
      `Their answer was saved.${when[i] ? '' : ' Date not recorded.'}`,
    ][i] } : {}),
    waitingFor: ended ? (o?.state === 'kept' ? 'No reply was recorded. You kept their current choice.' : o?.state === 'other' ? 'Sorted on their page. No answer recorded here.' : 'This check has ended. This step was not recorded.')
      : i < next && !facts[i] ? 'This step was not recorded.'
      : i === 0 ? 'Waiting for payment or a recorded payment message in Owed'
      : i === 1 ? 'Waiting for you to send and confirm the check'
      : c.state === 'ask' ? 'Waiting for you to check their reply'
      : c.state === 'wa_held' ? 'Their tap needs checking before you save it'
      : c.state === 'wa_undone' ? 'You undid their answer. Waiting for you to save their choice.' : 'Waiting for their reply',
  }))
  // not_sent only accepts opened, which is not a green send. There is no existing send Undo to expose here.
  return stepBar(steps, { label: 'Book again steps', ended, justCompleted, describe: async step => {
    if (step.key !== 'payment') return {}
    // Read existing scoped History only when tapped, never on every Messages/customer refresh.
    const dates = await Promise.all((c.payment_clean_ids ?? []).map(async id => {
      const data = await api<{ events: { before_json: string | null; after_json: string | null; created_at: string }[] }>(`/api/events?entity=cleans&entity_id=${id}&limit=500`)
      const event = data.events.find(e => {
        const before = JSON.parse(e.before_json ?? '{}'), after = JSON.parse(e.after_json ?? '{}')
        return after.payment_status === 'paid' && before.payment_status !== 'paid'
          || (after.chase_count ?? 0) > (before.chase_count ?? 0)
          || !!after.chased_on && after.chased_on !== before.chased_on
      })
      return event?.created_at
    }))
    return dates.length && dates.every(Boolean) ? { when: stepDate(dates.sort().at(-1)!), detail: `${c.first_paid ? 'Payment recorded as paid' : 'Payment no longer holds up Book again'}.` }
      : { detail: `${c.first_paid ? 'Payment recorded as paid' : 'Payment no longer holds up Book again'}. Date not recorded.` }
  } })
}

export function bookAgainCard(c: BACard, hooks: CardHooks): HTMLElement {
  const today = todayISO()
  const card = el('article', { class: `ba-card ba-${c.state}`, 'data-customer': String(c.customer_id) })
  const top = el('div', { class: 'ba-card-top' })
  if (hooks.where === 'messages') top.append(nameLink(c))
  else if (hooks.where === 'customer') top.append(el('h3', { class: 'ba-card-title' }, 'Book again'))
  if (hooks.where === 'step') card.classList.add('ba-in-step')
  else card.append(top)
  card.append(bookAgainSteps(c))
  const line = (text: string, cls = 'ba-line'): HTMLElement => el('p', { class: cls }, text)

  if (c.state === 'waits') {
    top.append(el('span', { class: 'ba-tag' }, 'After their payment'))
    card.append(line(`First clean ${c.first_date ? shortDate(c.first_date) : ''}${c.first_pence ? ` · ${money(c.first_pence)}` : ''} not paid yet`))
    const open = el('button', { type: 'button', class: 'link-button ba-next-toggle', 'aria-expanded': 'false' }, 'What happens next')
    const next = el('div', { class: 'ba-next', hidden: '' })
    next.append(el('div', { class: 'ba-steps' },
      step('pound', '1 · Their payment', 'Now', true), step('send', `2 · Check with ${her(c)}`, 'Then', false), step('reply', '3 · Their answer', 'Last', false)),
      owedLink(c),
      line('Payment messages stay in Owed, as they are today. Waiting cards like this one are not counted on the Messages tab.', 'ba-small'))
    open.addEventListener('click', () => {
      const shut = next.hidden
      next.hidden = !shut
      open.setAttribute('aria-expanded', String(shut))
    })
    card.append(open, next)
    return card
  }
  if (c.state === 'ready') {
    if (hooks.where === 'customer') card.append(line('Not sent yet', 'ba-state'), line(`Check ${her(c)} is happy with ${c.kind === 'regular' && c.weeks ? `every ${c.weeks} weeks` : 'cleaning again'}. Roundly asks you once, after their first clean.`))
    else if (hooks.where === 'messages') card.append(line(oftenWords(c), 'ba-state'), line(`First clean done${c.first_date ? ` ${shortDate(c.first_date)}` : ''} · ${c.first_paid ? 'paid' : c.payment_recorded ? 'payment message sent' : 'payment not recorded'}`))
    const go = el('button', { type: 'button', class: 'btn-primary ba-main' }, c.manual ? 'Check on WhatsApp' : `Check with ${her(c)}`)
    const slot = el('div', { class: 'ba-check-slot' })
    go.addEventListener('click', () => {
      const panel = hooks.where === 'messages' ? card.closest<HTMLElement>('.messages-panel') : null
      if (panel) { deskCheck = c.customer_id; go.hidden = true; openBookAgain(c, hooks.reload, slot) } else openBookAgain(c, hooks.reload)
    })
    card.append(go, slot)
    if (hooks.where === 'messages' && deskCheck === c.customer_id) {
      go.hidden = true; openBookAgain(c, hooks.reload, slot)
    }
    return card
  }
  if (c.state === 'opened' && c.offer) {
    const o = c.offer
    const mine = o.opened_by && getMe()?.email && o.opened_by === getMe()!.email
    card.append(line('WhatsApp opened', 'ba-state'), el('h4', { class: 'ba-ask' }, `Did you send ${her(c)} the check?`))
    if (o.no_thanks) card.append(line(`${her(c)} tapped No thanks on the WhatsApp message, so Roundly won't save an answer for them.`, 'ba-warn'))
    card.append(line(mine || !o.opened_by ? 'Roundly only knows WhatsApp opened, not whether you pressed Send.' : `Opened by ${o.opened_by} at ${clock(o.opened_at)}.`, 'ba-small'))
    const sent = el('button', { type: 'button', class: 'btn-primary ba-main' }, 'I sent it')
    const not = el('button', { type: 'button', class: 'btn-secondary' }, "Didn't send it")
    sent.addEventListener('click', () => void act(c, 'sent', {}, `${her(c)}: you sent the check. Roundly asks again in 3 days.`, hooks.reload, sent))
    not.addEventListener('click', () => void act(c, 'not_sent', {}, `${her(c)}: back to not sent yet`, hooks.reload, not))
    card.append(el('div', { class: 'ba-buttons' }, sent, not))
    return card
  }
  if ((c.state === 'quiet' || c.state === 'ask') && c.offer) {
    if (c.offer.no_thanks) card.append(line(`${her(c)} tapped No thanks on the WhatsApp message, so Roundly won't save an answer for them unless you say so.`, 'ba-warn'))
    card.append(line(c.state === 'ask' ? sentAgo(c.offer.sent_on, today) : `${sentAgo(c.offer.sent_on, today)}. Waiting for their answer.`, 'ba-state'))
    if (c.state === 'ask') card.append(el('h4', { class: 'ba-ask' }, `Any reply from ${her(c)}?`))
    else card.append(line(`Roundly asks you again ${c.offer.sent_on ? `on ${shortDate(plusDays(c.offer.sent_on, 3))}` : 'in 3 days'}. Tap their answer here if it comes sooner.`, 'ba-small'))
    card.append(answers(c, hooks))
    return card
  }
  if (c.state.startsWith('wa_') && c.offer) {
    waCard(c, card, hooks, line)
    return card
  }
  if (c.state === 'done' && c.offer) {
    card.append(line('Done', 'ba-state'), line(finishedWords(c), 'ba-line'))
    // Their answer can be taken back here at any time, not only from the bar (a reply lost to the signal still has an
    // Undo; Astra's second re-check).
    if (c.offer.state === 'answered') {
      const undo = el('button', { type: 'button', class: 'link-button' }, 'Undo their answer')
      undo.addEventListener('click', () => {
        undo.disabled = true
        void mutate({ label: `Undo Book again: ${c.name}`, method: 'POST', path: pathOf(c), body: { action: 'undo', version: c.offer!.version } }).then(async (u) => {
          const words = (u.row as { words?: string } | undefined)?.words
          toast(u.ok ? words ?? 'Undone.' : u.status === 0 ? 'Undo saves when there is signal' : u.error ?? 'That did not undo.')
          await loadBookAgain()
          hooks.reload()
        })
      })
      card.append(undo)
    }
    return card
  }
  return card
}

// ---- Book again on WhatsApp with tap buttons (0099; STAGE3-PLAN.md F) -------------------------------------------------
const STATUS_WORDS: Record<string, string> = { sent: 'Sent', delivered: 'Delivered', read: 'Read', failed: "Didn't arrive" }
const HELD_WHY: Record<string, string> = {
  customer_changed: 'their page changed since it went', visit_changed: 'their next visit changed since it went', late: 'it came more than 7 days later',
  undone: 'you undid their answer', old_buttons: 'it was on the earlier message', already_answered: 'they had already answered', moved_on: 'the check had moved on',
  no_thanks: 'they also said no thanks',
}

function waCard(c: BACard, card: HTMLElement, hooks: CardHooks, line: (t: string, cls?: string) => HTMLElement): void {
  const o = c.offer!
  const missingCycle = (): HTMLElement => line(`${her(c)} tapped a choice that needs checking. Check their reply in WhatsApp before saving a choice.`, 'ba-line')
  const done = (): HTMLButtonElement => {
    const b = el('button', { type: 'button', class: 'link-button' }, 'Done') as HTMLButtonElement
    b.addEventListener('click', () => void act(c, 'dismiss', {}, `${her(c)}: off Messages. It stays on their page.`, hooks.reload, b))
    return b
  }
  const sendYourself = (label: string): HTMLButtonElement => {
    const b = el('button', { type: 'button', class: 'btn-primary ba-main' }, label) as HTMLButtonElement
    b.addEventListener('click', () => openBookAgain(c, hooks.reload, null, true))
    return b
  }
  const close = (): HTMLButtonElement => {
    const b = el('button', { type: 'button', class: 'link-button' }, 'Leave it: no Book again for them') as HTMLButtonElement
    b.addEventListener('click', () => void act(c, 'close', {}, `${her(c)}: Book again closed. Roundly won't ask again.`, hooks.reload, b))
    return b
  }
  if (c.state === 'wa_unsure') {
    card.append(line("Roundly isn't sure it went", 'ba-state'),
      line(`WhatsApp didn't answer when the Book again message went to ${her(c)}. Look in WhatsApp on the work phone: if it's there, wait for their tap; if not, send it yourself.`, 'ba-small'))
    const again = el('button', { type: 'button', class: 'btn-secondary' }, 'Look again') as HTMLButtonElement
    again.addEventListener('click', () => { again.disabled = true; void loadBookAgain().then(hooks.reload) })
    card.append(el('div', { class: 'ba-buttons' }, sendYourself("It didn't go: send it yourself"), again), close())
    return
  }
  if (c.state === 'wa_failed') {
    card.append(line("It didn't go on WhatsApp", 'ba-state'), ...(o.wa_error ? [line(o.wa_error, 'ba-small')] : []))
    card.append(sendYourself('Send it yourself in WhatsApp'), close())
    return
  }
  if (c.state === 'wa_waiting') {
    card.append(line(`Sent on WhatsApp, waiting for ${her(c)}'s tap`, 'ba-state'),
      line(`${STATUS_WORDS[o.wa_status ?? 'sent'] ?? 'Sent'}${o.sent_at ? ` · ${shortDate(o.sent_at.slice(0, 10))}` : ''}. Their tap saves their choice here by itself. If they reply in words, tap their answer below.`, 'ba-small'))
    card.append(answers(c, hooks, true), done())
    return
  }
  if (c.state === 'wa_held') {
    card.append(line(`${her(c)} tapped, but Roundly didn't save it`, 'ba-state'))
    const box = el('div', { class: 'ba-answers' })
    card.append(box)
    void api<{ held?: HeldTap[] }>(pathOf(c)).then((r) => {
      for (const t of r.held ?? []) {
        if (t.weeks == null) { box.append(missingCycle()); continue }
        // After their No thanks, only on purpose (the server refuses otherwise).
        const despite = !!o.no_thanks
        const use = el('button', { type: 'button', class: 'btn-primary ba-main' }, `${despite ? 'Use it anyway' : 'Use it'}: ${pickedOften(t.weeks)}${t.pence ? ` at ${money(t.pence)}` : ''}`) as HTMLButtonElement
        use.addEventListener('click', () => void useTap(c, t, hooks, use, false, despite))
        if (despite) box.append(el('p', { class: 'ba-warn' }, `${her(c)} also tapped No thanks.`))
        box.append(el('p', { class: 'ba-line' }, `${her(c)} tapped ${pickedOften(t.weeks)}${t.why ? `, but ${HELD_WHY[t.why] ?? 'something changed since it went'}` : ''}.`), use)
      }
    }, () => box.append(line('No signal: look again when you have it.', 'ba-small')))
    card.append(done())
    return
  }
  if (c.state === 'wa_answered') {
    const thanks = o.picked_weeks === 0 ? `Thanks ${her(c)}, let us know when you'd like another clean. It will be ${money(o.picked_pence)} each time.` : `Thanks ${her(c)}, that's you on ${pickedOften(o.picked_weeks)} at ${money(o.picked_pence)}. See you next time.`
    // Their chat, with the words ready to paste: a reply there is free for 24 hours after their tap, and the chat itself
    // says when that has passed (wa-send.ts).
    const say = el('button', { type: 'button', class: 'btn-primary ba-main' }, `Send ${her(c)} a thank you`) as HTMLButtonElement
    say.addEventListener('click', () => {
      void copyText(thanks, { notice: 'Paste the words in their chat.', what: 'message' }).then(copied => {
        if (copied) window.location.hash = `#/chats?customer=${c.customer_id}`
      })
    })
    card.append(line(`${her(c)} tapped ${pickedOften(o.picked_weeks)}`, 'ba-state'),
      line(`Saved on their page: ${pickedOften(o.picked_weeks)} at ${money(o.picked_pence)}${o.picked_weeks ? ", with Roundly's suggested next clean" : ', when they ask'}.`, 'ba-line'),
      ...(o.no_thanks ? [line('They tapped No thanks after that: Roundly won\'t offer Book again again. Their choice stays unless you Undo it.', 'ba-warn')] : []),
      ...((o.held ?? 0) > 0 ? [line('They tapped another choice after that. To use it instead, Undo their answer first, then Use it.', 'ba-small')] : []),
      el('p', { class: 'ba-small' }, `A thank you is free on WhatsApp for 24 hours after their tap: "${thanks}"`), say)
    const undo = el('button', { type: 'button', class: 'link-button' }, 'Undo their answer') as HTMLButtonElement
    undo.addEventListener('click', () => {
      undo.disabled = true
      void mutate({ label: `Undo Book again: ${c.name}`, method: 'POST', path: pathOf(c), body: { action: 'undo', version: o.version } }).then(async (u) => {
        const words = (u.row as { words?: string } | undefined)?.words
        toast(u.ok ? words ?? 'Undone.' : u.status === 0 ? 'Undo saves when there is signal' : u.error ?? 'That did not undo.')
        await loadBookAgain()
        hooks.reload()
      })
    })
    card.append(el('div', { class: 'ba-links' }, undo, done()))
    return
  }
  if (c.state === 'wa_no') {
    card.append(line(`${her(c)} said no thanks`, 'ba-state'), line("Roundly won't offer them Book again. Their payment messages and chats carry on as before.", 'ba-small'))
    // They also tapped a choice: only the owner can use it, and says so.
    if ((o.held ?? 0) > 0) {
      const box = el('div', { class: 'ba-answers' })
      card.append(box)
      void api<{ held?: HeldTap[] }>(pathOf(c)).then((r) => {
        for (const t of r.held ?? []) {
          if (t.weeks == null) { box.append(missingCycle()); continue }
          const use = el('button', { type: 'button', class: 'btn-secondary' }, `They also tapped ${pickedOften(t.weeks)}: use it anyway`) as HTMLButtonElement
          use.addEventListener('click', () => void useTap(c, t, hooks, use, false, true))
          box.append(use)
        }
      }, () => undefined)
    }
    card.append(done())
    return
  }
  if (c.state === 'wa_undone') {
    card.append(line(`You undid ${her(c)}'s answer`, 'ba-state'), line('A tap on that message now only shows here. Tap their answer yourself, or leave it.', 'ba-small'), answers(c, hooks), close())
  }
}

async function useTap(c: BACard, t: HeldTap, hooks: CardHooks, button: HTMLButtonElement, overwrite = false, despite = false): Promise<void> {
  button.disabled = true
  try {
    await changeNow('POST', pathOf(c), { action: 'use_tap', tap_id: t.id, version: c.offer!.version, customer_version: c.customer_version, ...(overwrite ? { overwrite: true } : {}), ...(despite ? { despite_no_thanks: true } : {}) })
    toast(`${her(c)} is now ${pickedOften(t.weeks)}${t.pence ? ` at ${money(t.pence)}` : ''}`)
  } catch (err) {
    const e = err as ApiError
    button.disabled = false
    if (e.status === 409 && e.code === 'changed_since' && !overwrite) {
      const box = el('div', { class: 'ba-warn', role: 'alert' }, el('strong', {}, e.message || `${her(c)}'s price or how often changed since the message went.`),
        el('span', {}, `Save ${pickedOften(t.weeks)}${t.pence ? ` at ${money(t.pence)}` : ''} over it?`))
      const yes = el('button', { type: 'button', class: 'btn-primary' }, 'Save their choice')
      const no = el('button', { type: 'button', class: 'btn-secondary' }, 'Leave it')
      yes.addEventListener('click', () => { box.remove(); void useTap(c, t, hooks, button, true, despite) })
      no.addEventListener('click', () => box.remove())
      box.append(el('div', { class: 'ba-buttons' }, yes, no))
      button.before(box)
      return
    }
    toast(e.status === 0 ? 'No signal. Try again when you have it.' : e.message || 'That did not save.')
  }
  await loadBookAgain()
  hooks.reload()
}

// Opens Owed on her first clean, picked out (owed.ts openOwedFor), as "Open in Owed" on her page does.
function owedLink(c: BACard): HTMLElement {
  // The card's main button, so drawn as one (no link line).
  const go = el('button', { type: 'button', class: 'btn-primary ba-main' }, 'Go to their payment in Owed')
  go.addEventListener('click', () => void import('./screens/owed').then(({ openOwedFor }) => openOwedFor(c.first_clean_id)))
  return go
}

function step(drawing: Drawing, title: string, when: string, now: boolean): HTMLElement {
  return el('div', { class: `ba-step${now ? ' is-now' : ''}` }, ring(drawing, 20), el('strong', {}, title), el('span', {}, when))
}

const plusDays = (iso: string, n: number): string => {
  const d = new Date(`${iso}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}
function clock(at: string | null): string {
  if (!at) return ''
  const d = new Date(`${at.replace(' ', 'T')}Z`)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true }).replace(' ', '').toLowerCase()
}

// "You sent it 28 Oct. Kept every 8 weeks (31 Oct). Roundly won't ask again." (drawing 6.7)
function finishedWords(c: BACard): string {
  const o = c.offer!
  const sent = o.sent_on ? `You sent it ${shortDate(o.sent_on)}. ` : ''
  const on = o.answered_at ? ` (${shortDate(o.answered_at.slice(0, 10))})` : ''
  const end = " Roundly won't ask again."
  if (o.state === 'answered') return `${sent}You saved their choice: ${pickedOften(o.picked_weeks)} at ${money(o.picked_pence)}${on}.${end}`
  if (o.state === 'kept') return `${sent}No reply, kept ${oftenWords(c).toLowerCase()}${on}.${end}`
  if (o.state === 'other') return `${sent}Something else: sorted on their page${on}.${end}`
  if (o.sent_on) return `${sent}They said no${on}. Nothing on their page changed.${end}`
  return `Not this time${on}.${end}`
}

// Her answer (FINAL 10): the lemon "leave it" first, the other tiles, Said no, Something else.
function answers(c: BACard, hooks: CardHooks, waiting = false): HTMLElement {
  const box = el('div', { class: 'ba-answers' })
  const offered = offeredTiles(c)
  // A regular's own choice is "Keep" only while it went at the price and weeks they have now; changed on the tile
  // before it was sent, it is an answer of its own (Astra's build review 6).
  const others = offered.filter((t) => !(c.kind === 'regular' && t.mine && t.pence === c.reg_price_pence && t.weeks === c.weeks))
  // Waiting for a WhatsApp tap there is nothing to press yet, so closing it is never the big button (Sonnet's screen test).
  const keep = el('button', { type: 'button', class: waiting ? 'btn-secondary ba-keep' : 'btn-primary ba-main ba-keep' }, el('strong', {}, leaveWords(c)),
    el('span', {}, c.kind === 'regular' && c.weeks && !others.some((t) => t.mine) ? `No reply, or they said ${c.weeks} weeks is fine` : 'No reply: nothing on their page changes'))
  keep.addEventListener('click', () => void act(c, 'keep', {}, `${her(c)}: ${leaveWords(c).replace(/^No reply, /, '').toLowerCase()}. Roundly won't ask again.`, hooks.reload, keep))
  box.append(keep)
  if (others.length) {
    box.append(el('p', { class: 'ba-sub' }, c.kind === 'regular' ? 'They picked another' : 'They picked'))
    const row = el('div', { class: 'ba-tiles ba-answer-tiles' })
    for (const t of others) {
      const b = el('button', { type: 'button', class: 'ba-tile' }, el('span', { class: 'ba-weeks' }, String(t.weeks)), el('strong', {}, t.mine ? `Every ${t.weeks} weeks, new price` : `Every ${t.weeks} weeks`), el('span', {}, pounds(t.pence)))
      b.addEventListener('click', () => void saveAnswer(c, t, hooks, b))
      row.append(b)
    }
    box.append(row)
  }
  const no = el('button', { type: 'button', class: 'link-button' }, 'Said no')
  no.addEventListener('click', () => void act(c, 'said_no', {}, `${her(c)} said no. Nothing on their page changed.`, hooks.reload, no))
  // Something else (FINAL 10): on Messages it opens their page, where Price and Every are, and nothing closes yet; on
  // their page, once sorted there, "Sorted it: Done" closes the check (Astra's build review 14).
  const other = hooks.where === 'messages'
    ? el('a', { class: 'link-button ba-other', href: `#/customer/${c.customer_id}` }, 'Something else')
    : el('button', { type: 'button', class: 'link-button' }, 'Something else, sorted here: Done')
  if (other instanceof HTMLButtonElement) other.addEventListener('click', () => void act(c, 'other', {}, `${her(c)}: Book again done. Roundly won't ask again.`, hooks.reload, other))
  box.append(el('div', { class: 'ba-links' }, no, other))
  return box
}

function offeredTiles(c: BACard): BATile[] {
  if (!c.offer) return c.tiles.filter(t => t.weeks >= 1 && t.weeks <= 52)
  try {
    const p = JSON.parse(c.offer.prices_json) as { tiles?: BATile[] }
    return (p.tiles ?? []).filter((t) => !t.out && t.weeks >= 1 && t.weeks <= 52)
  } catch {
    return c.tiles.filter(t => t.weeks >= 1 && t.weeks <= 52)
  }
}

// ---- saving ----------------------------------------------------------------------------------------------------------
const pathOf = (c: BACard): string => `/api/book-again/${c.customer_id}`

async function act(c: BACard, action: string, extra: Record<string, unknown>, words: string, after: () => void, button: HTMLButtonElement | HTMLAnchorElement | null): Promise<Ack> {
  if (button instanceof HTMLButtonElement) button.disabled = true
  const ack = await mutate({ label: `Book again: ${c.name}`, method: 'POST', path: pathOf(c), body: { action, ...(c.offer ? { version: c.offer.version } : {}), ...extra } })
  if (ack.ok || ack.status === 0) {
    toast(ack.ok ? words : `${words}. No signal: it sends when it can`)
    await loadBookAgain()
    after()
  } else {
    if (button instanceof HTMLButtonElement) button.disabled = false
    toast(ack.status === 409 ? 'Something changed just now. Look again.' : ack.error ?? 'That did not save.')
    await loadBookAgain()
    after()
  }
  return ack
}

// Saved now (changeNow): their answer changes money, so it needs the server's yes and its words. An answer whose reply is
// lost to the signal is looked up again: saved, it is shown as saved with its Undo (Astra's re-check, new finding).
async function saveAnswer(c: BACard, t: BATile, hooks: CardHooks, button: HTMLButtonElement, overwrite = false): Promise<void> {
  button.disabled = true
  let row: (BAOffer & { next?: { date: string; moved: string } }) | undefined
  try {
    row = (await changeNow<{ row: BAOffer & { next?: { date: string; moved: string } } }>('POST', pathOf(c),
      { action: 'answer', version: c.offer!.version, weeks: t.weeks, customer_version: c.customer_version, ...(overwrite ? { overwrite: true } : {}) })).row
  } catch (err) {
    button.disabled = false
    const e = err as ApiError
    if (e.status === 409 && e.code === 'changed_since') { askOverwrite(c, t, hooks, button, e.message); return }
    if (e.status === 0) {
      const now = await api<{ card: BACard | null }>(pathOf(c)).then((r) => r.card, () => null)
      if (now?.offer?.state === 'answered' && now.offer.picked_weeks === t.weeks) row = now.offer
    }
    if (!row) {
      toast(e.status === 0 ? 'No signal. Their answer needs signal to save; try again when you have it.' : e.message || 'That did not save.')
      await loadBookAgain()
      hooks.reload()
      return
    }
  }
  await loadBookAgain()
  hooks.reload()
  showSaved(c, t, row?.next ?? null)
  // Undo waits for signal like any other change (the shared Undo bar is gone once tapped).
  offerUndo(`${her(c)} is now ${pickedOften(t.weeks)} at ${money(t.pence)}`, () => {
    void mutate({ label: `Undo Book again: ${c.name}`, method: 'POST', path: pathOf(c), body: { action: 'undo', version: row?.version ?? c.offer!.version + 1 } })
      .then(async (u) => {
        const words = (u.row as { words?: string } | undefined)?.words
        toast(u.ok ? words ?? 'Undone.' : u.status === 0 ? 'Undo saves when there is signal' : u.error ?? 'That did not undo.')
        await loadBookAgain()
        hooks.reload()
      })
  })
}

// A change to her price or how often since the check went (FINAL 11): asked first.
function askOverwrite(c: BACard, t: BATile, hooks: CardHooks, button: HTMLButtonElement, warning?:string): void {
  const box = el('div', { class: 'ba-warn', role: 'alert' },
    el('strong', {}, warning || `${her(c)}'s price or how often changed since you sent it.`),
    el('span', {}, `Save ${pickedOften(t.weeks)} at ${money(t.pence)} over it?`))
  const yes = el('button', { type: 'button', class: 'btn-primary' }, 'Save their choice')
  const no = el('button', { type: 'button', class: 'btn-secondary' }, 'Leave it')
  yes.addEventListener('click', () => { box.remove(); void saveAnswer(c, t, hooks, button, true) })
  no.addEventListener('click', () => box.remove())
  box.append(el('div', { class: 'ba-buttons' }, yes, no))
  button.closest('.ba-answers')?.prepend(box)
}

// "You saved her choice" (drawing 6.6): How often, Each clean and Next clean, each with what it was.
function showSaved(c: BACard, t: BATile, next: { date: string; moved: string } | null): void {
  const dialog = el('dialog', { class: 'booking-dialog quick-sheet ba-sheet ba-saved-sheet', 'aria-label': `You saved ${her(c)}'s choice` }) as HTMLDialogElement
  const close = closeButton()
  const facts = el('div', { class: 'ba-facts' },
    fact('How often', t.weeks ? `${t.weeks} weeks` : 'When they ask', c.kind === 'regular' && c.weeks && c.weeks !== t.weeks ? `was ${c.weeks}` : c.kind === 'regular' ? '' : `was ${oftenWords(c)}`),
    fact('Each clean', money(t.pence), c.reg_price_pence != null && c.reg_price_pence !== t.pence ? `was ${money(c.reg_price_pence)}` : ''),
    ...(next?.date ? [fact('Next clean', `w/c ${shortDate(mondayOf(next.date))}`, next.moved === 'kept' ? 'the day you agreed, left as it is' : 'day not agreed')] : []))
  dialog.append(el('header', { class: 'ba-sheet-head' }, el('span', { class: 'ba-face', 'aria-hidden': 'true' }, initialsOf(c.name)),
    el('div', {}, el('h2', {}, 'You saved their choice'), el('p', {}, `${c.name}`)), close),
    el('div', { class: 'ba-sheet-body' }, facts,
      el('p', { class: 'ba-small' }, "Saved on their page. Only Roundly's suggested next clean moved; a day you'd agreed with them stays put.")))
  const done = (): void => { if (dialog.open) dialog.close() }
  close.addEventListener('click', done)
  dialog.addEventListener('close', () => dialog.remove())
  dialog.addEventListener('click', (e) => { if (e.target === dialog) done() })
  dialog.setAttribute('tabindex', '-1')
  document.body.append(dialog)
  dialog.showModal()
  dialog.focus({ preventScroll: true })
}
// The kit's close: a soft grey disc with a drawn cross.
function closeButton(): HTMLButtonElement {
  return el('button', { type: 'button', class: 'ba-close', 'aria-label': 'Close' }, lineDrawing('cross', 16)) as HTMLButtonElement
}

function fact(label: string, value: string, was: string): HTMLElement {
  return el('div', { class: 'ba-fact' }, el('span', {}, label), el('strong', {}, value), ...(was ? [el('small', {}, was)] : []))
}

// ---- the pop-up: Book Sarah again? / Keep Sarah coming? -------------------------------------------------------------
// place: on a computer, the right-hand panel of Messages (drawings Desk2 to Desk4); the same content as the phone's pop-up.
// fallback: the free way after a WhatsApp message didn't go (0099): Open in WhatsApp saves it on the same check.
export function openBookAgain(c: BACard, after: () => void, place: HTMLElement | null = null, fallback = false): void {
  // A new customer on a round sending WhatsApp from Roundly: Send on WhatsApp with tap buttons, when it can go (0099).
  if (!fallback && !c.manual && c.kind !== 'regular' && !c.offer) {
    void api<{ wa?: WaWay }>(`${pathOf(c)}?wa=1`).then((r) => {
      if (r.wa?.ok) openTapWay(c, r.wa.facts, after, place)
      else openFreeWay(c, after, place, false)
    }, () => openFreeWay(c, after, place, false))
    return
  }
  openFreeWay(c, after, place, fallback)
}

// Drawing 2B on WhatsApp: the one-off price and the round's two saved cycles, the words as they'll see
// them, and Send on WhatsApp. Nothing goes until Send is pressed; the server checks everything again then.
function openTapWay(c: BACard, f: WaFacts, after: () => void, place: HTMLElement | null): void {
  const title = `Book ${her(c)} again?`
  const dialog = (place ? el('section', { class: 'ba-sheet ba-panel-sheet', 'aria-label': title }) : el('dialog', { class: 'booking-dialog quick-sheet ba-sheet', 'aria-label': title })) as HTMLDialogElement
  const close = closeButton()
  dialog.append(el('header', { class: 'ba-sheet-head' }, el('span', { class: 'ba-face', 'aria-hidden': 'true' }, initialsOf(c.name)),
    el('div', {}, el('h2', {}, title), el('p', {}, `First clean done ${c.first_date === todayISO() ? 'today' : c.first_date ? shortDate(c.first_date) : ''}`)), close))
  dialog.setAttribute('tabindex', '-1')
  const body = el('div', { class: 'ba-sheet-body' })
  dialog.append(body)
  // Exactly as the message says them (Sonnet's screen test: £45.00 here, £45 in the message).
  const ins = (p: number): string => bookAgainPrice(p, f.insidePence ?? null)
  const tile = (label: string, price: string, weeks: string | null, lemon: boolean, note: string | null): HTMLElement =>
    el('div', { class: `ba-tile ba-tile-fixed${lemon ? ' is-lemon' : ''}` }, el('span', { class: `ba-weeks${weeks ? '' : ' is-blank'}`, 'aria-hidden': weeks ? 'false' : 'true' }, weeks ?? ''), el('strong', {}, label), el('span', { class: 'ba-tile-price' }, price),
      ...(note ? [el('span', { class: 'ba-suggested' }, note)] : []))
  body.append(el('h3', { class: 'ba-question' }, 'What they will see'),
    el('div', { class: 'ba-tiles' }, tile('Just occasionally', ins(f.anchor), null, false, null), ...f.tiles.map((t, i) => tile(`Every ${t.weeks} weeks`, ins(t.pence), String(t.weeks), i === 0, null))),
    el('p', { class: 'ba-small' }, `Prices come from Your prices. Their buttons: ${f.tiles.map(t => `Every ${t.weeks} weeks`).join(', ')} and Just occasionally. Their tap saves their choice here.`),
    el('div', { class: 'ba-message ba-message-fixed', 'aria-label': 'The message' }, f.words))
  const error = el('p', { class: 'form-error', role: 'alert' })
  const send = el('button', { type: 'button', class: 'btn-primary ba-main' }, 'Send on WhatsApp') as HTMLButtonElement
  const not = el('button', { type: 'button', class: 'btn-secondary' }, 'Not this time')
  const yourself = el('button', { type: 'button', class: 'link-button' }, 'Send it yourself in WhatsApp instead')
  if (f.nextAt) {
    const at = new Date(`${f.nextAt.replace(' ', 'T')}Z`)
    error.textContent = `${her(c)} had a WhatsApp from you in the last day. Book again can go ${Number.isNaN(at.getTime()) ? 'tomorrow' : `from ${at.toLocaleString('en-GB', { weekday: 'short', hour: 'numeric', minute: '2-digit', hour12: true })}`}.`
    send.disabled = true
  }
  body.append(error, el('div', { class: 'ba-buttons' }, send, not), yourself)
  const shut = (): void => { if (place) { deskCheck = null; after() } else if (dialog.open) dialog.close() }
  close.addEventListener('click', shut)
  dialog.addEventListener('close', () => dialog.remove())
  const clientKey = crypto.randomUUID().replace(/-/g, '')
  send.addEventListener('click', () => {
    if (send.dataset.busy) return
    send.dataset.busy = '1'
    send.disabled = true
    send.textContent = 'Sending…'
    void changeNow<{ row?: BAOffer }>('POST', pathOf(c), { action: 'send_wa', facts_key: f.key, client_key: clientKey }).then((r) => {
      const st = r.row?.state
      toast(st === 'sent' ? `Sent to ${her(c)} on WhatsApp. Their tap saves their choice.` : st === 'failed' ? `It didn't go on WhatsApp. Look at ${her(c)}'s card.` : `Roundly isn't sure it went. Look at ${her(c)}'s card.`)
      shut()
      void loadBookAgain().then(after)
    }, (err: ApiError) => {
      delete send.dataset.busy
      send.textContent = 'Send on WhatsApp'
      send.disabled = err.code === 'too_soon'
      error.textContent = err.status === 0 ? 'No signal. Roundly needs signal to send this.' : err.message || 'Something changed just now. Close this and look again.'
    })
  })
  not.addEventListener('click', () => {
    shut()
    void act({ ...c, offer: null }, 'not_this_time', {}, `Not this time for ${her(c)}. Roundly won't ask again.`, after, null)
  })
  yourself.addEventListener('click', () => { if (place) { clear(place); openFreeWay(c, after, place, false) } else { dialog.close(); openFreeWay(c, after, null, false) } })
  if (place) { clear(place); place.append(dialog); return }
  document.body.append(dialog)
  dialog.showModal()
  dialog.focus({ preventScroll: true })
}

function openFreeWay(c: BACard, after: () => void, place: HTMLElement | null, fallback: boolean): void {
  const regular = c.kind === 'regular'
  const tiles: BATile[] = c.tiles.filter(t => t.weeks >= 1 && t.weeks <= 52).map((t) => ({ ...t, out: false }))
  const title = c.manual ? `Make ${her(c)} a regular?` : regular ? `Keep ${her(c)} coming?` : `Book ${her(c)} again?`
  const dialog = (place ? el('section', { class: 'ba-sheet ba-panel-sheet', 'aria-label': title }) : el('dialog', { class: 'booking-dialog quick-sheet ba-sheet', 'aria-label': title })) as HTMLDialogElement
  const close = closeButton()
  const sub = regular && c.weeks
    ? `Already every ${c.weeks} weeks${c.reg_price_pence ? ` at ${money(c.reg_price_pence)}` : ''}`
    : c.manual ? `${oftenWords(c)} now${c.first_date ? ` · last clean ${shortDate(c.first_date)}` : ''}`
    : `First clean done ${c.first_date === todayISO() ? 'today' : c.first_date ? shortDate(c.first_date) : ''}`
  dialog.append(el('header', { class: 'ba-sheet-head' }, el('span', { class: 'ba-face', 'aria-hidden': 'true' }, initialsOf(c.name)),
    el('div', {}, el('h2', {}, title), el('p', {}, sub)), close))
  dialog.setAttribute('tabindex', '-1')
  const body = el('div', { class: 'ba-sheet-body' })
  dialog.append(body)
  const question = el('h3', { class: 'ba-question' }, regular && c.weeks ? `Check they're happy with ${c.weeks} weeks` : 'What should they see?')
  const row = el('div', { class: 'ba-tiles' })
  const change = el('button', { type: 'button', class: 'link-button' }, 'Change prices')
  const prices = el('div', { class: 'ba-prices', hidden: '' })
  const words = el('textarea', { class: 'ba-message', rows: '7', 'aria-label': 'The message' }) as HTMLTextAreaElement
  // The message always says the prices on the tiles (Astra's build review 5): a change to a tile or a price writes it
  // again, even after the words were changed (and says so), and Open checks every price is in it.
  let edited = false
  words.addEventListener('input', () => { edited = true })
  const rewrote = el('p', { class: 'ba-small', hidden: '' }, 'The message was written again with the new prices.')
  let lastPrices = ''
  const priceKey = (): string => JSON.stringify(tiles.map((t) => [t.weeks, t.pence, !!t.out]))
  const mismatch = (): string | null => {
    // Each price beside its own weeks ("every 4 weeks: £36.00", "every 8 weeks at £38.00"), never only somewhere in it.
    const beside = (t: BATile): boolean => new RegExp(`\\b${t.weeks} weeks[^£$0-9]{0,12}${money(t.pence).replace(/[.$]/g, '\\$&')}`, 'i').test(words.value)
    const missing = tiles.filter((t) => !t.out && t.pence != null && !beside(t))
    return missing.length ? `The message doesn't say every ${missing[0]!.weeks} weeks at ${money(missing[0]!.pence)}. Put the price in, or change a tile to write it again.` : null
  }
  const warn = el('div', { class: 'ba-warn' }, el('strong', {}, 'Send it yourself in WhatsApp'),
    el('span', {}, `Roundly puts the words in WhatsApp, but you press Send there. Roundly can't see ${her(c)}'s reply or save their choice until you tap their answer here.`))
  // A button, not a link: nothing to open in another tab before the check is claimed (Astra's re-check 3).
  const open = el('button', { type: 'button', class: 'btn-primary ba-main ba-open' }, 'Open in WhatsApp') as HTMLButtonElement
  const not = el('button', { type: 'button', class: 'btn-secondary' }, 'Not this time')
  const error = el('p', { class: 'form-error', role: 'alert' })
  body.append(question, row, change, prices, el('label', { class: 'ba-message-label' }, el('span', {}, 'The message'), words), rewrote, warn, error, el('div', { class: 'ba-buttons' }, open, not))

  const ready = (): string | null => {
    const on = tiles.filter((t) => !t.out)
    if (!on.length) return 'Keep at least one choice in.'
    const missing = on.find((t) => t.pence == null || t.pence <= 0)
    return missing ? `Add a price for every ${missing.weeks} weeks, or leave it out.` : null
  }
  const draw = (): void => {
    clear(row)
    for (const t of tiles) {
      const lemon = regular ? t.mine : t.weeks === 8
      const b = el('button', { type: 'button', class: `ba-tile${lemon ? ' is-lemon' : ''}${t.out ? ' is-out' : ''}`, 'aria-pressed': String(!t.out) },
        el('span', { class: 'ba-weeks' }, String(t.weeks)), el('strong', {}, tileLabel(t, c)), el('span', { class: 'ba-tile-price' }, pounds(t.pence)),
        el('span', { class: 'ba-tick', 'aria-hidden': 'true' }, ...(t.out ? [] : [lineDrawing('tick', 16)])))
      if (!regular && t.weeks === 8) b.append(el('span', { class: 'ba-suggested' }, 'Suggested'))
      b.addEventListener('click', () => {
        if (!t.out && tiles.filter((x) => !x.out).length === 1) { error.textContent = 'Keep at least one choice in.'; return }
        t.out = !t.out
        error.textContent = ''
        draw()
      })
      row.append(b)
    }
    const key = priceKey()
    if (!edited || key !== lastPrices) {
      if (edited && lastPrices) rewrote.hidden = false
      words.value = bookAgainMessage(c, tiles)
      edited = false
    }
    lastPrices = key
    open.setAttribute('aria-disabled', ready() ? 'true' : 'false')
  }
  // Change prices (BUILD-SPEC: "Change prices" under the tiles opens the three prices to type).
  for (const t of tiles) {
    const input = el('input', { inputmode: 'decimal', 'aria-label': `Every ${t.weeks} weeks`, value: t.pence != null ? (t.pence / 100).toFixed(2) : '' }) as HTMLInputElement
    input.addEventListener('input', () => { const p = parseMoney(input.value); t.pence = p != null && p > 0 ? p : null; draw() })
    prices.append(el('label', { class: 'ba-price-field' }, el('span', {}, `Every ${t.weeks} weeks`), input))
  }
  change.addEventListener('click', () => { prices.hidden = !prices.hidden; change.textContent = prices.hidden ? 'Change prices' : 'Done changing prices' })
  if (tiles.some((t) => t.pence == null)) { prices.hidden = false; change.textContent = 'Done changing prices' }
  draw()

  const shut = (): void => { if (place) { deskCheck = null; after() } else if (dialog.open) dialog.close() }
  close.addEventListener('click', shut)
  dialog.addEventListener('close', () => dialog.remove())
  // Open in WhatsApp claims the check FIRST (FINAL 12; Astra's build review 3): only once the server says it is theirs does
  // WhatsApp open, so two owners, or a page left open since things changed, never both send it. A computer's WhatsApp Web
  // tab is opened at the tap (a browser allows that only then) and sent on, or shut, once the answer lands.
  void sendingSettings().catch(()=>{})
  open.addEventListener('click', (e) => {
    e.preventDefault()
    if (open.dataset.busy) return
    const why = ready() ?? mismatch()
    const href = why ? null : whatsappHref(c.wa ?? '', words.value)
    if (why || !href) { error.textContent = why ?? `There is no WhatsApp number for ${her(c)}.`; return }
    const tab = whatsappTarget(href) === '_blank' ? window.open('about:blank', '_blank') : null
    open.setAttribute('aria-disabled', 'true')
    open.dataset.busy = '1'
    const body = { action: fallback ? 'fallback_free' : 'open', tiles: tiles.map((t) => ({ weeks: t.weeks, pence: t.pence, mine: t.mine, out: !!t.out })), message: words.value.trim(),
      ...(c.manual ? { manual: true } : {}), ...(fallback && c.offer ? { version: c.offer.version } : {}) }
    void changeNow<{ row?: { wa?: string; [key: string]: unknown } }>('POST', pathOf(c), body).then((r) => {
      // The number Roundly checked just now, not the one on the page when it opened (Astra, 29 Oct 2026).
      const to = (r.row?.wa ? whatsappHref(r.row.wa, words.value) : null) ?? href
      if (tab) tab.location.href = to
      else window.location.href = to
      if (!onAPhone() && r.row) {
        const reserved = { ...c, offer: r.row } as unknown as BACard
        confirmComputerSend(() => { shut();void act(reserved,'sent',{},`${her(c)}: you sent the check.`,after,null) },
          () => { tab?.close();void act(reserved,'not_sent',{},`${her(c)}: back to not sent yet`,after,null).then(ack=>{if(ack.ok && fallback)c.offer=ack.row as unknown as BAOffer;delete open.dataset.busy;open.setAttribute('aria-disabled','false')}) },`WhatsApp opened with your message to ${her(c)}.`)
      } else { shut();void loadBookAgain().then(after) }
    }, (err: ApiError) => {
      tab?.close()
      delete open.dataset.busy
      open.setAttribute('aria-disabled', 'false')
      error.textContent = err.status === 0 ? 'No signal. Roundly needs signal to open WhatsApp for this one.'
        : err.code === 'opened' ? `Someone else opened WhatsApp for ${her(c)} first. Close this and look at the card.`
        : err.message || 'Something changed just now. Close this and look again.'
    })
  })
  not.addEventListener('click', () => {
    shut()
    if (fallback) void act(c, 'close', {}, `${her(c)}: Book again closed. Roundly won't ask again.`, after, null)
    else void act({ ...c, offer: null }, 'not_this_time', c.manual ? { manual: true } : {}, `Not this time for ${her(c)}. Roundly won't ask again.`, after, null)
  })
  if (place) { clear(place); place.append(dialog); return }
  document.body.append(dialog)
  dialog.showModal()
  dialog.focus({ preventScroll: true })
}

// ---- Messages' group -------------------------------------------------------------------------------------------------
// Payment messages first (FINAL 3): its own group, its count apart; a card waiting on her payment shown, never counted.
// On a phone each card is drawn in the list. On a computer (FINAL 15, drawings Desk1 to Desk4) the list has a short row
// per customer and the chosen one's card, or its check, fills Messages' right-hand panel, as Owed does.
let deskOpen: number | null = null
let deskCheck: number | null = null
const onComputer = (): boolean => typeof window.matchMedia === 'function' && !window.matchMedia('(max-width:760px)').matches

// Messages' own rows were chosen: Book again's panel lets go.
export function bookAgainLetGo(): void { deskOpen = null; deskCheck = null }

function bookAgainRowStatus(c: BACard): string {
  // Each WhatsApp state says itself, as its card does (Sonnet's screen test: every one read "Check with").
  const wa: Partial<Record<string, string>> = { wa_unsure: 'Not sure it went', wa_failed: "Didn't go on WhatsApp", wa_waiting: 'Waiting for their tap',
    wa_held: 'Tapped: needs you', wa_answered: c.offer?.picked_weeks ? `Tapped every ${c.offer.picked_weeks} weeks` : c.offer?.picked_weeks === 0 ? 'Tapped just occasionally' : 'Tapped', wa_no: 'Said No thanks', wa_undone: 'Answer undone' }
  const words = c.state === 'waits' ? 'After their payment' : c.state === 'opened' ? 'WhatsApp opened' : c.state === 'ask' ? 'Any reply?' : wa[c.state] ?? `Check with ${her(c)}`
  return `${oftenWords(c)} · ${c.state === 'waits' ? 'Waits · ' : ''}${words}`
}

function deskRow(c: BACard, reload: () => void): HTMLElement {
  const row = el('button', { type: 'button', class: `ba-row${deskOpen === c.customer_id ? ' is-open' : ''}`, 'aria-expanded': String(deskOpen === c.customer_id) },
    el('span', { class: 'ba-face ba-face-sm', 'aria-hidden': 'true' }, initialsOf(c.name)),
    el('span', { class: 'ba-row-words' }, el('strong', {}, c.name), el('small', {}, bookAgainRowStatus(c))),
    ...(c.state === 'waits' ? [el('span', { class: 'ba-tag' }, 'Waits')] : []))
  row.addEventListener('click', () => { deskOpen = c.customer_id; deskCheck = null; reload() })
  return row
}

export function bookAgainTileItems(): { id: string; price: number | null }[] | null {
  if (!bookAgainShown()) return []
  return known ? known.cards.filter(c => c.counted).map(c => ({ id: `c${c.customer_id}`, price: c.reg_price_pence })) : null
}
export function bookAgainSection(root: HTMLElement, reload: () => void, query = '', onlyDue = false, sheetRow?: (id: string, name: string, card: () => HTMLElement, customerId: number, status: string) => HTMLElement): void {
  if (!bookAgainShown() || !known?.on || !known.cards.length) return
  const sec = el('section', { class: 'rebook-stage ba-section', 'aria-label': 'Book again' })
  const waiting = known.cards.filter((c) => c.state === 'waits').length
  const count = known.count
  sec.append(el('div', { class: 'rebook-stage-head' }, el('h2', { class: 'rebook-stage-title' }, meaningIcon('comingup', 'row'), 'Book again'),
    el('span', { class: 'ba-count' }, count ? String(count) : `${waiting} waiting`)))
  const list = el('div', { class: 'ba-list' })
  const desk = onComputer()
  if (deskOpen !== null && !known.cards.some((c) => c.customer_id === deskOpen)) deskOpen = null
  for (const c of known.cards.filter(c => (!query || c.name.toLowerCase().includes(query.toLowerCase())) && (!onlyDue || c.counted))) {
    if (sheetRow) list.append(sheetRow(`ba:${c.customer_id}`, c.name, () => bookAgainCard(c, { reload, where: 'messages' }), c.customer_id, bookAgainRowStatus(c)))
    else list.append(desk ? deskRow(c, reload) : bookAgainCard(c, { reload, where: 'messages' }))
  }
  if (!list.children.length) return
  sec.append(list)
  root.append(sec)
}

// On a computer, the chosen Book again card for Messages' right-hand panel (null: Messages shows its own).
export function bookAgainPanel(reload: () => void): HTMLElement | null {
  if (!onComputer() || deskOpen === null || !known?.on) return null
  const c = known.cards.find((x) => x.customer_id === deskOpen)
  if (!c) return null
  const card = bookAgainCard(c, { reload, where: 'messages' })
  card.classList.add('ba-desk-card')
  if (deskCheck === c.customer_id && c.state === 'ready') {
    const host = el('div', { class: 'ba-desk-card' })
    openBookAgain(c, reload, host)
    return host
  }
  return card
}

// ---- the customer page's card (drawing 6.7) --------------------------------------------------------------------------
// reloadPage: her answer changes Price and Every above it, so the whole page is read again.
export async function customerBookAgain(customerId: number, host: HTMLElement, reloadPage: () => void): Promise<void> {
  if (getMe()?.role !== 'owner') return
  const draw = async (): Promise<void> => {
    let card: BACard | null = null
    try {
      card = (await api<{ on: boolean; card: BACard | null }>(`/api/book-again/${customerId}`)).card
    } catch {
      return
    }
    clear(host)
    host.hidden = !card
    if (card) host.append(bookAgainCard(card, { reload: reloadPage, where: 'customer' }))
  }
  await draw()
}

// The customer page's Up next reads their card with the rest of the page (owner only); null when there is none.
// undefined: the read failed, so Up next must not take a missing step as done (Astra, 29 Oct 2026).
export async function fetchCustomerBookAgain(customerId: number): Promise<BACard | null | undefined> {
  if (getMe()?.role !== 'owner') return null
  try {
    return (await api<{ on: boolean; card: BACard | null }>(`/api/book-again/${customerId}`)).card
  } catch {
    return undefined
  }
}

// "Cleaned about every 6 weeks": the usual gap between their last few cleans, in whole weeks, or null with under three.
export function usualGapWeeks(dates: string[] | undefined): number | null {
  const d = (dates ?? []).slice().sort().reverse()
  if (d.length < 3) return null
  const gaps: number[] = []
  for (let i = 0; i + 1 < d.length; i++) gaps.push((Date.parse(d[i]!) - Date.parse(d[i + 1]!)) / (7 * 86_400_000))
  gaps.sort((a, b) => a - b)
  const w = Math.round(gaps[Math.floor(gaps.length / 2)]!)
  return w >= 1 && w <= 26 ? w : null
}
